import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X, ArrowRightLeft, AlertTriangle } from 'lucide-react';
import { doc, updateDoc, collection, addDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'PENDING — Väntar BRF-granskning' },
  { value: 'APPROVED', label: 'APPROVED — BRF godkänt, väntar ägare' },
  { value: 'REJECTED', label: 'REJECTED — Avvisad av BRF' },
  { value: 'ADDITIONAL_INFO', label: 'ADDITIONAL_INFO — Komplettering begärd' },
  { value: 'TENANT_INFO_SUBMITTED', label: 'TENANT_INFO_SUBMITTED — Hyresgästinfo klar' },
  { value: 'CREDIT_CHECK_COMPLETE', label: 'CREDIT_CHECK_COMPLETE — Kreditkontroll klar' },
  { value: 'AWAITING_OWNER_DECISION', label: 'AWAITING_OWNER_DECISION — Väntar ägarbeslut' },
  { value: 'FINAL_APPROVED', label: 'FINAL_APPROVED — Slutgiltigt godkänd' },
  { value: 'OWNER_REJECTED', label: 'OWNER_REJECTED — Avvisad av ägare' },
  { value: 'FINAL_REJECTED', label: 'FINAL_REJECTED — Slutgiltigt avvisad' },
];

export default function ChangeStatusModal({ open, onClose, application, onSuccess }) {
  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (application) {
      setNewStatus(application.status || '');
      setReason('');
      setError('');
    }
  }, [application]);

  const handleSave = async () => {
    if (!application?.id) return;
    if (!newStatus) {
      setError('Välj en status');
      return;
    }
    if (newStatus === application.status) {
      setError('Status är oförändrad');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const uid = auth.currentUser?.uid;
      const now = new Date().toISOString();

      await updateDoc(doc(db, 'applications', application.id), {
        status: newStatus,
        lastStatusChangeBy: uid || null,
        lastStatusChangeAt: now,
        lastStatusChangeReason: reason || null,
      });

      await addDoc(collection(db, 'audit_logs'), {
        action: 'manual_status_change',
        applicationId: application.id,
        previousStatus: application.status || null,
        newStatus,
        reason: reason || null,
        performedBy: uid || null,
        timestamp: now,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError('Kunde inte uppdatera status. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  if (!application) return null;

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} className="fixed z-50 inset-0 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
      <Dialog.Panel className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg">
        <button
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500"
          onClick={onClose}
          disabled={saving}
          aria-label="Stäng"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4 mb-5">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <ArrowRightLeft className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <Dialog.Title className="text-xl font-bold text-[#222222]">
              Ändra status manuellt
            </Dialog.Title>
            <p className="text-sm text-[#626262] mt-1">Admin-override som loggas i audit_logs</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 flex gap-2 text-sm text-amber-900">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>Detta överskrider normala övergångar. Använd endast när nödvändigt.</div>
        </div>

        <div className="space-y-2 mb-4 p-4 bg-zinc-50 rounded-lg text-sm">
          <div className="flex items-center justify-between">
            <span className="text-zinc-500">Ansökan:</span>
            <span className="font-medium text-zinc-700">
              {application.ownerName || 'Okänd ägare'} · Lgh {application.apartmentNumber || 'N/A'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-zinc-500">Nuvarande status:</span>
            <span className="px-2.5 py-1 text-xs font-mono bg-zinc-200 text-zinc-800 rounded">
              {application.status || 'OKÄND'}
            </span>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-[#222222] mb-2">Ny status</label>
          <select
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            disabled={saving}
            className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="mb-5">
          <label className="block text-sm font-medium text-[#222222] mb-2">
            Anledning <span className="text-zinc-400 font-normal">(loggas)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="T.ex. BRF behöver granska om, återställer steg"
            rows={3}
            disabled={saving}
            className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9] resize-none"
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2.5 border border-[#E2EAF0] text-[#626262] rounded-lg font-semibold hover:bg-[#F9FCFF] transition-colors disabled:opacity-50"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-[#62B8E9] text-white rounded-lg font-semibold hover:bg-[#4A9FD1] transition-colors disabled:opacity-50"
          >
            {saving ? 'Sparar...' : 'Spara'}
          </button>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
}
